import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DataTable from '../../components/admin/DataTable';
import ImageUpload from '../../components/admin/ImageUpload';

const GalleryManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    images: [],
    category: 'عام',
    album_type: 'photos',
    is_published: true,
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gallery/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${BACKEND_URL}/api/gallery/${editing.id}` : `${BACKEND_URL}/api/gallery/`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchItems();
        resetForm();
        alert('تم الحفظ بنجاح');
      }
    } catch (error) {
      alert('حدث خطأ');
    }
  };

  const resetForm = () => {
    setFormData({
      title_ar: '',
      title_en: '',
      description_ar: '',
      description_en: '',
      images: [],
      category: 'عام',
      album_type: 'photos',
      is_published: true,
    });
    setEditing(null);
    setShowForm(false);
  };

  const columns = [
    {
      header: 'الصورة',
      render: (row) => (
        <img src={row.images?.[0] || row.thumbnail} alt="" className="w-16 h-16 object-cover rounded" />
      ),
    },
    { header: 'العنوان', accessor: 'title_ar' },
    { header: 'النوع', accessor: 'album_type' },
    { header: 'عدد الصور', render: (row) => row.images?.length || 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E]">إدارة معرض الصور</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0F388E]">
          <Plus className="ml-2 w-5 h-5" />
          إضافة ألبوم
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editing ? 'تعديل' : 'إضافة'} ألبوم</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0F388E]">المعلومات الأساسية</h3>
                <input
                  type="text"
                  placeholder="العنوان بالعربية *"
                  value={formData.title_ar}
                  onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
                <input
                  type="text"
                  placeholder="Title in English"
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                <select
                  value={formData.album_type}
                  onChange={(e) => setFormData({...formData, album_type: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="photos">صور</option>
                  <option value="videos">فيديوهات</option>
                </select>
              </div>

              {/* Images Section for Photos */}
              {formData.album_type === 'photos' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#0F388E]">الصور</h3>
                  <ImageUpload
                    label="إضافة صورة جديدة"
                    value=""
                    category="gallery"
                    onChange={(url) => {
                      if (url) {
                        setFormData({
                          ...formData,
                          images: [...(formData.images || []), url]
                        });
                      }
                    }}
                  />
                  
                  {/* Display existing images */}
                  {formData.images && formData.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {formData.images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={img} 
                            alt={`صورة ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.images.filter((_, i) => i !== index);
                              setFormData({...formData, images: newImages});
                            }}
                            className="absolute top-2 left-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Videos Section for Videos */}
              {formData.album_type === 'videos' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#0F388E]">الفيديوهات</h3>
                  <p className="text-sm text-gray-600">
                    أضف روابط فيديوهات YouTube أو Vimeo
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      id="video-url-input"
                      className="flex-1 px-4 py-2 border rounded-lg"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('video-url-input');
                        if (input.value) {
                          setFormData({
                            ...formData,
                            images: [...(formData.images || []), input.value]
                          });
                          input.value = '';
                        }
                      }}
                    >
                      إضافة
                    </Button>
                  </div>
                  
                  {/* Display existing videos */}
                  {formData.images && formData.images.length > 0 && (
                    <div className="space-y-3 mt-4">
                      {formData.images.map((video, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                          <div className="flex-1 truncate">
                            <a 
                              href={video} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm"
                            >
                              {video}
                            </a>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newVideos = formData.images.filter((_, i) => i !== index);
                              setFormData({...formData, images: newVideos});
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>إلغاء</Button>
                <Button type="submit" className="bg-[#0F388E]">حفظ</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable
            columns={columns}
            data={items}
            onEdit={(item) => { setEditing(item); setFormData(item); setShowForm(true); }}
            onDelete={async (item) => {
              await fetch(`${BACKEND_URL}/api/gallery/${item.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              });
              fetchItems();
            }}
            loading={loading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default GalleryManagement;
